// Browser-native text-to-speech for the AI persona's replies.
// Uses the Web Speech API (speechSynthesis) — the synthesis counterpart
// to the recognition already driving react-speech-recognition. No
// backend changes, no API cost.

const MUTED_KEY = '***';

export const isMuted = (): boolean => {
  try {
    return localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
};

export const setMuted = (muted: boolean): void => {
  try {
    localStorage.setItem(MUTED_KEY, muted ? '1' : '0');
  } catch {
    /* private mode etc. — muting just won't persist */
  }
};

/** Strip markdown noise so the voice reads prose, not syntax. */
export const cleanForSpeech = (text: string): string =>
  text
    .replace(/```[\s\S]*?```/g, ' code block omitted ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/[*_#>|]+/g, ' ')
    // Some voices literally pronounce "semicolon"/"colon"; swap them for
    // a comma (keeps the pause, kills the word) and drop other symbols.
    .replace(/[;:]/g, ',')
    .replace(/[()[\]{}"'`]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Prefer a male English voice when the browser offers one. Voice lists
 * differ per OS/browser, so match known male voice names and fall back
 * to the platform default.
 */
const MALE_VOICE_HINTS = [
  'alex', 'daniel', 'fred', 'oliver', 'jorge', 'diego', 'aaron', 'arthur',
  'rishi', 'male', 'google uk english male', 'google us english male',
];

export const pickMaleVoice = (
  voices: SpeechSynthesisVoice[],
): SpeechSynthesisVoice | null => {
  const english = voices.filter((v) => v.lang.toLowerCase().startsWith('en'));
  for (const hint of MALE_VOICE_HINTS) {
    const match = english.find((v) => v.name.toLowerCase().includes(hint));
    if (match) return match;
  }
  return null;  // caller keeps the default voice
};

/**
 * Sentence-ish chunks. Chrome's speechSynthesis can silently stall on
 * very long utterances, so replies are queued sentence by sentence.
 */
export const chunkForSpeech = (text: string, maxLen = 220): string[] => {
  const sentences = text.match(/[^.!?]+[.!?]+|\S[^.!?]*$/g) ?? [text];
  const chunks: string[] = [];
  let current = '';
  for (const sentence of sentences) {
    const piece = sentence.trim();
    if (!piece) continue;
    if (current && (current + ' ' + piece).length > maxLen) {
      chunks.push(current);
      current = piece;
    } else {
      current = current ? current + ' ' + piece : piece;
    }
  }
  if (current) chunks.push(current);
  return chunks;
};

// Chrome (and Safari) return an EMPTY voice list until 'voiceschanged'
// has fired at least once — which is exactly the state right after a
// speech-recognition session ends. Without this cache the voice reply
// fell back to the platform default (female) while typed replies, which
// happen later, got the male voice. Cache the list as soon as the page
// knows it, and defer speech until voices are known.
let voiceCache: SpeechSynthesisVoice[] = [];
let voicesSubscribed = false;

const refreshVoiceCache = (): void => {
  const list = window.speechSynthesis.getVoices?.() ?? [];
  if (list.length > 0) voiceCache = list;
};

const ensureVoiceSubscription = (): void => {
  if (voicesSubscribed) return;
  const synth = window.speechSynthesis;
  if (typeof synth.addEventListener !== 'function') return;
  voicesSubscribed = true;
  synth.addEventListener('voiceschanged', refreshVoiceCache);
  refreshVoiceCache();
};

const speakChunks = (chunks: string[]): void => {
  const voices = voiceCache.length > 0 ? voiceCache : (window.speechSynthesis.getVoices?.() ?? []);
  const voice = pickMaleVoice(voices);
  window.speechSynthesis.cancel();  // never overlap the previous reply
  for (const chunk of chunks) {
    const utterance = new SpeechSynthesisUtterance(chunk);
    utterance.rate = 1.15;   // slightly brisker than the default cadence
    if (voice) utterance.voice = voice;
    window.speechSynthesis.speak(utterance);
  }
};

export const speakText = (text: string): void => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  if (isMuted()) return;
  const cleaned = cleanForSpeech(text);
  if (!cleaned) return;
  ensureVoiceSubscription();
  const chunks = chunkForSpeech(cleaned);
  const known = voiceCache.length > 0 || (window.speechSynthesis.getVoices?.() ?? []).length > 0;
  if (known) {
    speakChunks(chunks);
    return;
  }
  // Voices not loaded yet (typical right after the mic stops): wait for
  // 'voiceschanged' so the male voice is used consistently, with a
  // timeout fallback so speech is never lost on browsers that never
  // fire the event.
  const synth = window.speechSynthesis;
  let done = false;
  const go = () => {
    if (done || isMuted()) return;
    done = true;
    synth.removeEventListener?.('voiceschanged', go);
    refreshVoiceCache();
    speakChunks(chunks);
  };
  synth.addEventListener?.('voiceschanged', go);
  window.setTimeout(go, 300);
};

export const stopSpeaking = (): void => {
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
};

/** Test hook: clear the module-level voice cache between cases. */
export const __resetVoiceState = (): void => {
  voiceCache = [];
  voicesSubscribed = false;
};
